import { hydrate } from "../hydration/index.js";
/**
 * Channel Webhook Class
 */
export class ChannelWebhook {
    #collection;
    id;
    /**
     * Construct Channel Webhook
     * @param collection Collection
     * @param id Webhook
     */
    constructor(collection, id) {
        this.#collection = collection;
        this.id = id;
    }
    /**
     * Whether this object exists
     */
    get $exists() {
        return !!this.#collection.getUnderlyingObject(this.id).id;
    }
    /**
     * Webhook name
     */
    get name() {
        return this.#collection.getUnderlyingObject(this.id).name;
    }
    /**
     * Webhook avatar
     */
    get avatar() {
        return this.#collection.getUnderlyingObject(this.id).avatar;
    }
    /**
     * Webhook avatar URL
     */
    get avatarURL() {
        return this.#collection
            .getUnderlyingObject(this.id)
            .avatar?.createFileURL();
    }
    /**
     * Channel ID this webhook belongs to
     */
    get channelId() {
        return this.#collection.getUnderlyingObject(this.id).channelId;
    }
    /**
     * Channel this webhook belongs to
     */
    get channel() {
        return this.#collection.client.channels.get(this.#collection.getUnderlyingObject(this.id).channelId);
    }
    /**
     * Secret token for sending messages to this webhook
     */
    get token() {
        return this.#collection.getUnderlyingObject(this.id).token;
    }
    /**
     * Edit this webhook
     */
    async edit(data) {
        // Session-authed route, NOT the `/webhooks/{id}/{token}` variant.
        //
        // The gateway only routes POST on the token path (webhook *fire*); GET,
        // PATCH and DELETE there fall through to a 404. This method was calling
        // the token path, so Channel Settings -> Webhooks -> Save silently 404'd
        // and the edit never persisted.
        //
        // Fixing it toward `/webhooks/{id}` rather than adding token-authed
        // management routes is deliberate: the caller here always has a session,
        // and the id-only route already enforces ManageWebhooks. Putting a
        // long-lived management credential in the URL path would also leak it
        // into access logs and Referer headers for no benefit.
        const webhook = await this.#collection.client.api.patch(`/webhooks/${this.id}`, data);
        this.#collection.updateUnderlyingObject(this.id, hydrate("channelWebhook", webhook, this.#collection.client));
    }
    /**
     * Delete this webhook
     */
    async delete() {
        // Session-authed route — see the note on edit() above. The token variant
        // 404s, which made Channel Settings -> Webhooks -> Delete a no-op.
        await this.#collection.client.api.delete(`/webhooks/${this.id}`);
        this.#collection.delete(this.id);
    }
}
